import { useId, useState } from 'react';

// Every phase a run reports, in order (StudioRun.phase; `done` follows `persisting`). An art
// run (contract 2.7.0 proposal) designs the composition after writing its text, then (2.9.0)
// fills its image places: the question, the generation and the placement.
export const RUN_STEPS = ['brief', 'evidence', 'story', 'writing', 'review', 'persisting'];
export const ART_RUN_STEPS = ['brief', 'evidence', 'story', 'writing', 'designing', 'images', 'review', 'persisting'];
// The art wording of the phases that differ from a script's.
const ART_LABELS = { writing: 'artPhase_writing', designing: 'artPhase_designing', images: 'artPhase_images', review: 'artPhase_review', persisting: 'artPhase_persisting' };
export const phaseKey = (phase, art) => (art && ART_LABELS[phase]) || `phase_${phase}`;
const WORKING = ['queued', 'running'];

// The line a run shows: its current phase while it works, else its status.
export function runLabelKey(run, art = false) {
  const working = WORKING.includes(run.status);
  if (run.outcome === 'answer') return working ? 'phase_answer' : `status_${run.status}`;
  if (working) return phaseKey(run.phase === 'done' ? 'persisting' : run.phase, art);
  return art && run.status === 'succeeded' ? 'artStatus_succeeded' : `status_${run.status}`;
}

// While a run works, its line is the current phase. Otherwise it is the run status,
// and the phase it stopped at stays pending in the expanded list. A run that answers
// in the chat (contract 1.7.0, `outcome: answer`) has no script steps: its line says
// it is replying, and its result is the answer message itself.
export default function useRunProgress(run, art = false) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const working = WORKING.includes(run.status);
  const succeeded = run.status === 'succeeded';
  const answering = run.outcome === 'answer';
  const steps = answering ? [] : art ? ART_RUN_STEPS : RUN_STEPS;
  const index = succeeded || run.phase === 'done' ? steps.length : Math.max(steps.indexOf(run.phase), 0);
  const current = steps[Math.min(index, steps.length - 1)];
  return {
    open,
    toggle: () => setOpen(value => !value),
    listId,
    working,
    answered: answering && succeeded,
    labelKey: working ? (answering ? 'phase_answer' : phaseKey(current, art)) : art && succeeded ? 'artStatus_succeeded' : `status_${run.status}`,
    done: steps.slice(0, index),
    upcoming: succeeded ? [] : steps.slice(working ? index + 1 : index),
  };
}
